import { API_CONFIG } from '../config/api';
import { marketDataParser } from './MarketDataParser';
import marketDataStore from './MarketDataStore';

class WebSocketManager {
  constructor() {
    this.ws = null;
    this.subscriptions = new Map();
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3600;
    this.reconnectDelay = 1000;
    this.marketDataCallbacks = new Map();
    this.orderUpdateCallbacks = new Set(); 
    this.tradeUpdateCallbacks = new Set();
    this.heartbeatInterval = null;
    this.marketDataParser = marketDataParser;
    this.connectionListeners = new Set();
  }

  isAuthValid() {
    const token = localStorage.getItem('auth_token');
    const loginId = localStorage.getItem('login_id');
    return !!(token && loginId);
  }

  connect() {
    try {
      if (!this.isAuthValid()) {
        console.warn('WebSocketManager: Skipping connection - No valid session found.');
        return;
      }

      const token = localStorage.getItem('auth_token');
      const loginId = localStorage.getItem('login_id');
  
      const wsUrl = `${API_CONFIG.WSS_URL}/ws/v1/feeds?login_id=${loginId}&token=${token}`;
  
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }
  

  setupEventHandlers() {
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this.notifyConnectionStatus(true);
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.startHeartbeat();
      
      // Resubscribe to all active subscriptions
      if (this.marketDataSubscriber) {
        this.marketDataSubscriber.resubscribeAll();
      }
      this.subscribeToOrderUpdates();
    };

    this.ws.onclose = () => {
      this.notifyConnectionStatus(false);
      this.stopHeartbeat();
      this.handleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    // this.ws.onmessage = (event) => {
    //   try {
    //     if (event.data instanceof ArrayBuffer) {
    //       const marketData = this.marketDataParser.parseDetailedData(event.data);
    //       if (marketData) {
    //         this.notifyMarketDataSubscribers(marketData);
    //       }
    //     } else {
    //       const data = JSON.parse(event.data);
    //       if (data.hasOwnProperty('s')) {
    //         console.log('Subscription Response:', {
    //           status: data.s,
    //           message: data.message || 'Subscription successful'
    //         });
    //       }
    //     }
    //   } catch (error) {
    //     console.error('Error handling WebSocket message:', error);
    //   }
    // };
    this.ws.onmessage = (event) => {
      try {
        if (event.data instanceof ArrayBuffer) {
          this.handleArrayBufferMessage(event.data);
        } else {
          const data = JSON.parse(event.data);
          if (data.hasOwnProperty('s')) {
            console.log('Subscription Response:', {
              status: data.s,
              message: data.message || 'Subscription successful',
            });
          }
        }
      } catch (error) {
        console.error('Error handling WebSocket message:', error);
      }
    };
  }
  handleArrayBufferMessage(buffer) {
    const mode = new DataView(buffer).getInt8(0); // Get the mode from byte 0
    if (mode === 50) {
      // Order updates
      const jsonPacket = new TextDecoder().decode(buffer.slice(5));
      const orderUpdate = JSON.parse(jsonPacket);
      this.notifyOrderUpdateSubscribers(orderUpdate);
    } else if (mode === 51) {
      // Trade updates
      const jsonPacket = new TextDecoder().decode(buffer.slice(5));
      const tradeUpdate = JSON.parse(jsonPacket);
      this.notifyTradeUpdateSubscribers(tradeUpdate);
    } else {
      const marketData = this.marketDataParser.parseDetailedData(buffer);
      if (marketData) {
        this.notifyMarketDataSubscribers(marketData);
      }
    }
  }

  // Step 2: Subscribe to Trade Updates
subscribeToTradeUpdates() {
  const clientId = localStorage.getItem('login_id');
  if (!clientId) {
    console.error('Client ID not available for trade updates subscription');
    return;
  }

  const payload = JSON.stringify({
    a: 'subscribe',
    v: [clientId, 'web'],
    m: 'updates',
  });

  if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
    this.ws.send(payload);
    console.log('Subscribed to trade updates');
  } else {
    console.warn('WebSocket not connected, queuing subscription for trade updates');
  }
}

// Step 3: Notify Trade Update Subscribers
notifyTradeUpdateSubscribers(tradeUpdate) {
  const trades = Array.isArray(tradeUpdate?.result) ? tradeUpdate.result : [tradeUpdate?.result];

  const buyTrades = trades.filter(trade => trade.order_side === "BUY");
  const sellTrades = trades.filter(trade => trade.order_side === "SELL");


  // Notify all registered callbacks with the grouped trades
  this.tradeUpdateCallbacks.forEach(callback =>
    callback({ buyTrades, sellTrades })
  );
}

// Step 4: Add and Remove Trade Update Callbacks
addTradeUpdateCallback(callback) {
  this.tradeUpdateCallbacks.add(callback);
}

removeTradeUpdateCallback(callback) {
  this.tradeUpdateCallbacks.delete(callback);
}

  subscribeToOrderUpdates() {
    const clientId = localStorage.getItem('login_id');
    if (!clientId) {
      console.error('Client ID not available for order updates subscription');
      return;
    }

    const payload = JSON.stringify({
      a: 'subscribe',
      v: [clientId, 'web'],
      m: 'updates',
    });

    if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(payload);
      console.log('Subscribed to order updates');
    } else {
      console.warn('WebSocket not connected, queuing subscription for order updates');
    }
  }

  notifyOrderUpdateSubscribers(orderUpdate) {
    // Convert the single order object into an array for consistency
    const orders = Array.isArray(orderUpdate) ? orderUpdate : [orderUpdate];
  
    const pendingOrders = orders.filter(order =>
      [
        "CONFIRMED",
        "OPEN PENDING",
        "MODORDER",
        "CANORDER",
        "NEWORDER",
        "MODCONFIRMED",
        "NOT MODIFIED",
        "MODIFIED",
        "CANREJECTED",
        "MODREJECTED",
        "VALIDATIONPENDING",
        "PARTIALTRADE",
        "PARTIAL TRADE",
        "PARTIAL_TRADE",
        "AMO_REQ_RECEIVED",
        "AMO_REQ_CONFIRMED",
        "AMO_REQ_MODIFIED",
        "AMO_NEW_CONFIRMED",
        "AMO_MODIFY_CONFIRMED",
        "OPEN",
        "TRIGGER PENDING",
        "AFTER MARKET ORDER REQ RECEIVED",
        "MODIFY AFTER MARKET ORDER REQ RECEIVED",
      ].includes(order.order_status)
    );
  
    const completedOrders = orders.filter(order =>
      [
        "COMPLETE",
        "COMPLETED",
        "REJECTED",
        "CANCONFIRMED",
        "AMO_CANCEL_CONFIRMED",
        "CANCELLED AFTER MARKET ORDER",
        "CANCELLED",
      ].includes(order.order_status)
    );
  
    // Notify all callbacks
    this.orderUpdateCallbacks.forEach(callback =>
      callback({ pendingOrders, completedOrders })
    );
  }
  

  addOrderUpdateCallback(callback) {
    this.orderUpdateCallbacks.add(callback);
  }

  removeOrderUpdateCallback(callback) {
    this.orderUpdateCallbacks.delete(callback);
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('{"a":"h","v":[],"m":""}');
      }
    }, 9500);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  handleReconnect() {
    // Guard: Only attempt reconnection if the user is still logged in
    if (!this.isAuthValid()) {
      console.log('WebSocketManager: User logged out, stopping reconnection attempts.');
      return;
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      setTimeout(() => {
        console.log(`Attempting to reconnect... (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
        this.reconnectAttempts++;
        this.reconnectDelay *= 2; // Exponential backoff
        this.connect();
      }, this.reconnectDelay);
    }
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.isConnected = false;
    }
  }

  onMarketData(key, callback) {
    if (!this.marketDataCallbacks.has(key)) {
      this.marketDataCallbacks.set(key, new Set());
    }
    this.marketDataCallbacks.get(key).add(callback);
  }

  removeMarketDataCallback(key, callback) {
    const callbacks = this.marketDataCallbacks.get(key);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.marketDataCallbacks.delete(key);
      }
    }
  }

  notifyMarketDataSubscribers(data) {
    const key = `${data.exchange}_${data.token}`;
    const callbacks = this.marketDataCallbacks.get(key);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  addConnectionListener(listener) {
    this.connectionListeners.add(listener);
    // Notify current status immediately
    listener(this.isConnected);
  }

  removeConnectionListener(listener) {
    this.connectionListeners.delete(listener);
  }

  notifyConnectionStatus(status) {
    this.isConnected = status;
    this.connectionListeners.forEach(listener => listener(status));
  }

  handleMessage(message) {
    const data = JSON.parse(message.data);
    
    if (data.type === 'marketData') {
        const parsedData = marketDataParser.parseDetailedData(data);
        
        // Trigger any additional callbacks if needed
        if (this.onMarketDataUpdate) {
            this.onMarketDataUpdate(parsedData);
        }
    }
    // Handle other message types...
  }

  isConnectionReady() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  sendMessage(message) {
    if (!this.isConnectionReady()) {
      console.warn('WebSocket not ready, message not sent:', message);
      return false;
    }
    
    try {
      this.ws.send(message);
      return true;
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      return false;
    }
  }
}

export const wsManager = new WebSocketManager(); 